// @flow
import {
  type FileSpecification as CsvFileSpecification,
  type ParsedCsv,
  type CellValue as CsvCellValue,
  parsed as parsedCsv,
  cellValue as csvCellValue,
  rowCount as csvRowCount,
  estimatedColumnCount as csvEstimatedColumnCount,
} from 'modules/csv';
import {
  type WorkSheet,
  type Cell as BaseXlsCell,
  parsed as parsedXls,
  cell as xlsCell,
  worksheetRowCount,
  worksheetColumnCount,
  simpleCellValue as xlsSimpleCellValue,
} from 'modules/xls';
import {
  type FileSpecification as FixedWidthTextFileSpecification,
  type FixedWidthTextFile as FixedFile,
  type Cell as BaseFixedWidthTextCell,
  parsed as parsedFixedWidthTextFile,
  cell as fixedWidthFileParsedCell,
} from 'modules/fixed-width-text-file';

export type XlsxFileSpecification = $ReadOnly<{|
  sheet?: null | string,
|}>;

export type XlsFile = $ReadOnly<{|
  type: 'Xls',
  specification: XlsxFileSpecification,
|}>;

export type CsvFile = $ReadOnly<{|
  type: 'Csv',
  specification: CsvFileSpecification,
|}>;

export type FixedWidthTextFile = $ReadOnly<{|
  type: 'FixedWidthText',
  specification: FixedWidthTextFileSpecification,
|}>;

export type TabularFileSpecification = XlsFile | CsvFile | FixedWidthTextFile;

export type TabularFileExtents = $ReadOnly<{|
  rows: number,
  columns: number,
|}>;

export type ParsedCsvFile = $ReadOnly<{|
  type: 'ParsedCsv',
  csv: ParsedCsv,
|}>;

export type ParsedXlsFile = $ReadOnly<{|
  type: 'ParsedXls',
  sheet: WorkSheet,
|}>;

export type ParsedFixedWidthTextFile = $ReadOnly<{|
  type: 'FixedWidthText',
  file: FixedFile,
|}>;

export type TabularFile =
  | ParsedCsvFile
  | ParsedXlsFile
  | ParsedFixedWidthTextFile;
export type ParseOptions = $ReadOnly<{|
  lineLimit?: number,
  runInWorker?: boolean,
|}>;

export type CellAddress = $ReadOnly<{|
  row: number,
  column: number,
|}>;

export type CsvCell = $ReadOnly<{|
  type: 'CsvCell',
  cell: CsvCellValue,
|}>;

export type XlsCell = $ReadOnly<{|
  type: 'XlsCell',
  cell: BaseXlsCell,
|}>;

export type FixedWidthTextCell = $ReadOnly<{|
  type: 'FixedWidthTextCell',
  cell: BaseFixedWidthTextCell,
|}>;

export type TabularCell = XlsCell | CsvCell | FixedWidthTextCell;

export const parsed = (
  file: Blob,
  spec: TabularFileSpecification,
  opts?: ParseOptions = { ...null },
): Promise<TabularFile> => {
  switch (spec.type) {
    case 'Csv':
      return parsedCsv(file, {
        lineLimit: opts.lineLimit,
        runInWorker: opts.runInWorker,
        fileSpecification: spec.specification,
      }).then((result) => ({
        type: 'ParsedCsv',
        csv: result,
      }));
    case 'Xls':
      return parsedXls(file, {
        sheetRows: opts.lineLimit,
      }).then((workbook) => ({
        type: 'ParsedXls',
        sheet:
          workbook.Sheets[spec.specification.sheet || workbook.SheetNames[0]],
      }));
    case 'FixedWidthText':
      return parsedFixedWidthTextFile(file, spec.specification, {
        lineLimit: opts.lineLimit,
      }).then((result) => ({
        type: 'FixedWidthText',
        file: result,
      }));
    default:
      // eslint-disable-next-line babel/no-unused-expressions
      (spec.type: empty);
      throw new Error(`Unhandled TabularFile type: ${spec.type}`);
  }
};

export const csvParsedCellByAddress =
  (file: ParsedCsvFile): ((address: CellAddress) => CsvCell | null) =>
  (address: CellAddress): CsvCell | null => {
    const cell = csvCellValue(file.csv)(address.row, address.column);

    if (cell === null) return null;

    return {
      type: 'CsvCell',
      cell,
    };
  };

export const xlsParsedCellByAddress =
  (file: ParsedXlsFile): ((address: CellAddress) => XlsCell | null) =>
  (address: CellAddress): XlsCell | null => {
    const cell = xlsCell(file.sheet)({ r: address.row, c: address.column });

    if (cell === null) return null;

    return {
      type: 'XlsCell',
      cell,
    };
  };

export const fixedWidthFileParsedCellByAddress =
  (
    file: ParsedFixedWidthTextFile,
  ): ((address: CellAddress) => FixedWidthTextCell | null) =>
  (address: CellAddress): FixedWidthTextCell | null => ({
    type: 'FixedWidthTextCell',
    cell: fixedWidthFileParsedCell(file.file)(address.row, address.column),
  });

export const cellByAddress =
  (file: TabularFile): ((address: CellAddress) => TabularCell | null) =>
  (address: CellAddress): TabularCell | null => {
    switch (file.type) {
      case 'ParsedCsv':
        return csvParsedCellByAddress(file)(address);
      case 'ParsedXls':
        return xlsParsedCellByAddress(file)(address);
      case 'FixedWidthText':
        return fixedWidthFileParsedCellByAddress(file)(address);
      default:
        // eslint-disable-next-line babel/no-unused-expressions
        (file.type: empty);
        throw new Error(`Unhandled TabularFile type: ${file.type}`);
    }
  };

export const csvExtents = (file: ParsedCsvFile): TabularFileExtents => ({
  rows: csvRowCount(file.csv),
  columns: csvEstimatedColumnCount(file.csv),
});

export const xlsExtents = (file: ParsedXlsFile): TabularFileExtents => ({
  rows: worksheetRowCount(file.sheet),
  columns: worksheetColumnCount(file.sheet),
});

export const fixedWidthTextExtents = (
  file: ParsedFixedWidthTextFile,
): TabularFileExtents => ({
  rows: file.file.lines.length,
  columns: file.file.lines[0].length,
});

export const extents = (file: TabularFile): TabularFileExtents => {
  switch (file.type) {
    case 'ParsedCsv':
      return csvExtents(file);
    case 'ParsedXls':
      return xlsExtents(file);
    case 'FixedWidthText':
      return fixedWidthTextExtents(file);
    default:
      // eslint-disable-next-line babel/no-unused-expressions
      (file.type: empty);
      throw new Error(`Unhandled TabularFile type: ${file.type}`);
  }
};

export const cellValue = (
  cell: TabularCell,
): string | number | boolean | Date => {
  switch (cell.type) {
    case 'CsvCell':
      return cell.cell;
    case 'XlsCell':
      return xlsSimpleCellValue(cell.cell);
    case 'FixedWidthTextCell':
      return cell.cell.value;
    default:
      // eslint-disable-next-line babel/no-unused-expressions
      (cell.type: empty);
      throw new Error(`Unhandled TabularCell type: ${cell.type}`);
  }
};

export const specificationFromFile = (file: Blob): TabularFileSpecification => {
  if (file.type === 'text/csv') {
    return {
      type: 'Csv',
      specification: { ...null },
    };
  }

  return {
    type: 'Xls',
    specification: { ...null },
  };
};
